import React from 'react';
import { Box, Typography, Button, IconButton, Stack } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { IPaymentCard } from '../../types/global/generic.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CircularCheckmark from '../CustomSVGIcons/CircularCheckIcon';
import MasterCard from '../CustomSVGIcons/MasterCard';
import VisaCard from '../CustomSVGIcons/VisaCard';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';

export interface PaymentCardProps {
    isDefault?: boolean;
    isExpired?: boolean;
    card?: IPaymentCard;
    onSetDefault?: () => void;
    onDelete?: () => void;
    isModalMode?: boolean;
    isSelected?: boolean;
    cardSize?: string;
    /** Billing Account Hub list styling */
    variant?: 'default' | 'obsidian';
}

const PaymentCard: React.FC<PaymentCardProps> = ({
    card,
    isDefault = false,
    isExpired = false,
    onSetDefault,
    onDelete,
    isModalMode,
    isSelected,
    cardSize,
    variant = 'default',
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const isObsidian = variant === 'obsidian';
    const compactBreakpointSx = {
        '@media (min-width: 900px) and (max-width: 1200px)': {
            fontSize: '0.75rem',
        },
    };
    const last4 = card?.authorization?.last4 ?? '';
    const month = String(card?.authorization?.exp_month ?? '').padStart(2, '0');
    const year = card?.authorization?.exp_year ?? '';
    const brand = card?.authorization?.brand;
    let CardRender = MasterCard;
    if (brand === 'visa') {
        CardRender = VisaCard;
    }

    const primaryText = isObsidian ? OBSIDIAN.onSurface : t.primary_white;
    const mutedText = isObsidian ? OBSIDIAN.onSurfaceVariant : t.tag_white;
    const accent = isObsidian ? OBSIDIAN.primary : t.highlight;
    const displayFont = isObsidian ? OBSIDIAN_DISPLAY_FONT : FONT_ENUM.MAIN_BOLD;
    const bodyFont = isObsidian ? OBSIDIAN_BODY_FONT : FONT_ENUM.MAIN_BOLD;

    const innerRowSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: { xs: 'nowrap', sm: 'nowrap' } as const,
        flexDirection: { xs: 'column', sm: 'row' } as const,
        width: '100%',
        minWidth: 0,
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: isObsidian ? '12px' : 2,
        border: isObsidian ? `1px solid ${OBSIDIAN.surfaceContainerHighest}` : '1px solid rgba(255,255,255,0.1)',
        bgcolor: isObsidian ? OBSIDIAN.surfaceContainerLow : 'rgba(255,255,255,0.06)',
        boxSizing: 'border-box' as const,
        transition: isObsidian ? 'border-color 0.2s ease, background-color 0.2s ease' : undefined,
        ...(isObsidian &&
            !isExpired && {
                '&:hover': {
                    borderColor: 'rgba(255,201,101,0.28)',
                    bgcolor: OBSIDIAN.surfaceContainerHigh,
                },
            }),
        ...(isExpired && {
            opacity: 0.5,
            filter: 'grayscale(1)',
        }),
        '@media (min-width: 900px) and (max-width: 1200px)': {
            p: 2,
            gap: 1.25,
        },
    };

    const brandBoxSx = {
        width: 48,
        height: 32,
        bgcolor: isObsidian ? OBSIDIAN.surfaceContainerHighest : '#1a1a1a',
        borderRadius: isObsidian ? '8px' : 1,
        border: isObsidian ? `1px solid ${OBSIDIAN.outline}33` : '1px solid rgba(63, 63, 70, 1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '@media (max-width: 599px)': {
            width: 40,
            height: 28,
        },
    };

    if (isModalMode) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    width: '100%',
                    minWidth: 0,
                    py: 0.5,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                    <Box
                        sx={{
                            ...brandBoxSx,
                            width: cardSize ? 40 : 48,
                            height: cardSize ? 28 : 32,
                            overflow: 'hidden',
                        }}>
                        <CardRender
                            width={cardSize ?? '44px'}
                            height={cardSize ?? '44px'}
                            stroke={isExpired ? t.grey : t.tag_white}
                        />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                color: t.tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {`${brand ?? ''} ending in ${last4}`}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 11,
                                color: isExpired ? 'error.main' : t.tag_white,
                                opacity: isExpired ? 1 : 0.8,
                            }}>
                            Exp. {month}/{year}
                        </Typography>
                    </Box>
                </Box>
                {isSelected && (
                    <Box sx={{ flexShrink: 0 }}>
                        <CircularCheckmark filled color={t.highlight} size={20} />
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={innerRowSx}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1.25, sm: 2.5 },
                    minWidth: 0,
                    flex: 1,
                    width: '100%',
                }}>
                <Box sx={{ ...brandBoxSx, overflow: 'hidden' }}>
                    <CardRender width="44px" height="30px" stroke={isExpired ? t.grey : mutedText} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            letterSpacing: '0.2em',
                            color: isExpired ? t.grey : primaryText,
                            fontFamily: displayFont,
                            ...compactBreakpointSx,
                            whiteSpace: 'nowrap',
                            '@media (max-width: 599px)': {
                                fontSize: '0.75rem',
                                letterSpacing: '0.12em',
                            },
                        }}>
                        •••• {last4}
                    </Typography>
                    {!isExpired ? (
                        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.625rem',
                                    fontFamily: bodyFont,
                                    fontWeight: 700,
                                    color: mutedText,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.12em',
                                    '@media (max-width: 599px)': {
                                        fontSize: '0.55rem',
                                        letterSpacing: '0.08em',
                                    },
                                }}>
                                Exp {month}/{year}
                            </Typography>
                            {isDefault && (
                                <Stack direction="row" alignItems="center" gap={0.5}>
                                    <Box
                                        sx={{
                                            width: 4,
                                            height: 4,
                                            borderRadius: '50%',
                                            bgcolor: isObsidian ? OBSIDIAN.secondary : t.green_status_indicator_full,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '0.625rem',
                                            fontFamily: bodyFont,
                                            fontWeight: 700,
                                            color: isObsidian ? OBSIDIAN.secondary : t.green_status_indicator_full,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.12em',
                                        }}>
                                        Active
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    ) : (
                        <Typography
                            sx={{
                                fontSize: '0.625rem',
                                fontFamily: bodyFont,
                                fontWeight: 700,
                                color: 'error.main',
                                textTransform: 'uppercase',
                                letterSpacing: '0.12em',
                                mt: 0.5,
                            }}>
                            Expired {month}/{year}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Stack
                direction="row"
                alignItems="center"
                justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                gap={1}
                sx={{ flexShrink: 0, flexWrap: 'nowrap', width: { xs: '100%', sm: 'auto' } }}>
                {!isExpired &&
                    (isDefault ? (
                        <Button
                            disabled
                            disableRipple
                            startIcon={
                                <CheckCircleIcon
                                    sx={{
                                        fontSize: 18,
                                        color: accent,
                                        '&.MuiSvgIcon-root': { fill: 'currentColor' },
                                    }}
                                />
                            }
                            sx={{
                                color: accent,
                                fontSize: '0.625rem',
                                fontWeight: 700,
                                fontFamily: bodyFont,
                                textTransform: 'uppercase',
                                letterSpacing: '0.12em',
                                px: 1.5,
                                py: 0.75,
                                minWidth: 0,
                                border: 'none',
                                '@media (max-width: 599px)': {
                                    fontSize: '0.55rem',
                                    letterSpacing: '0.08em',
                                    px: 1,
                                    py: 0.5,
                                },
                                '&.Mui-disabled': {
                                    color: accent,
                                    border: 'none',
                                    opacity: 1,
                                },
                            }}>
                            Default
                        </Button>
                    ) : (
                        <Button
                            variant="text"
                            disableRipple
                            onClick={onSetDefault}
                            sx={{
                                color: accent,
                                fontSize: '0.625rem',
                                fontWeight: 700,
                                fontFamily: bodyFont,
                                textTransform: 'uppercase',
                                letterSpacing: '0.12em',
                                px: 1.5,
                                py: 0.75,
                                minWidth: 0,
                                borderRadius: 1,
                                '@media (max-width: 599px)': {
                                    fontSize: '0.55rem',
                                    letterSpacing: '0.08em',
                                    px: 1,
                                    py: 0.5,
                                },
                                '&:hover': {
                                    bgcolor: isObsidian ? 'rgba(255,201,101,0.08)' : 'rgba(255,255,255,0.08)',
                                },
                                '@media (min-width: 900px) and (max-width: 1200px)': {
                                    fontSize: '0.5625rem',
                                    px: 1,
                                },
                            }}>
                            Set as Default
                        </Button>
                    ))}
                <IconButton
                    aria-label="delete card"
                    onClick={onDelete}
                    size="small"
                    sx={{
                        color: mutedText,
                        opacity: 0.85,
                        ml: { xs: 0.5, sm: 0 },
                        '&:hover': {
                            color: 'error.main',
                            bgcolor: 'transparent',
                        },
                    }}>
                    <DeleteOutlinedIcon
                        sx={{
                            fontSize: 22,
                            '@media (max-width: 599px)': { fontSize: 20 },
                            '@media (min-width: 900px) and (max-width: 1200px)': { fontSize: 18 },
                        }}
                    />
                </IconButton>
            </Stack>
        </Box>
    );
};

export default PaymentCard;
